import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          localStorage.removeItem("game_cart");
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState("failed");
          return;
        }
      } catch (err) {
        setState("error");
        return;
      }
      tries += 1;
      if (tries < 15) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#08080C] bg-grid flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="w-full max-w-lg border border-[#242436] bg-[#111118] p-10 text-center shadow-[0_4px_40px_rgba(0,0,0,0.6)]">
        <img src="/logo.webp" alt="G.A.M.E. logo" className="h-16 w-auto mx-auto rounded-sm ring-1 ring-[#FFB800]/60" />
        {state === "checking" && (
          <>
            <Loader2 className="w-10 h-10 text-[#8B5CF6] animate-spin mx-auto mt-8" />
            <h1 className="font-display font-black uppercase text-2xl text-[#F4F4F6] mt-4">Confirming payment...</h1>
            <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#62627A] mt-2" data-testid="payment-status-text">Hold tight — verifying with Stripe</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-10 h-10 text-[#FFB800] mx-auto mt-8" />
            <h1 className="font-display font-black uppercase text-2xl text-[#F4F4F6] mt-4">Payment confirmed</h1>
            <p className="text-sm text-[#A1A1B3] leading-relaxed mt-3" data-testid="payment-status-text">
              Thank you for backing the mission. A Stripe receipt is on its way to your inbox —
              donations support G.A.M.E. Mentorship and Training Inc.; store purchases are fulfilled by G.A.M.E. Training LLC.
            </p>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-10 h-10 text-[#FFB800] animate-spin mx-auto mt-8" />
            <h1 className="font-display font-black uppercase text-2xl text-[#F4F4F6] mt-4">Payment processing</h1>
            <p className="text-sm text-[#A1A1B3] leading-relaxed mt-3" data-testid="payment-status-text">Your payment is still being processed. Your Stripe receipt will confirm it by email.</p>
          </>
        )}
        {(state === "failed" || state === "error") && (
          <>
            <XCircle className="w-10 h-10 text-[#8B5CF6] mx-auto mt-8" />
            <h1 className="font-display font-black uppercase text-2xl text-[#F4F4F6] mt-4">Payment not completed</h1>
            <p className="text-sm text-[#A1A1B3] leading-relaxed mt-3" data-testid="payment-status-text">Nothing was charged. You can try again anytime.</p>
          </>
        )}
        <div className="mt-8 flex justify-center gap-4">
          <Link to="/" data-testid="success-home-link" className="font-mono text-xs uppercase tracking-[0.2em] border border-[#3D3D58] px-5 py-2.5 text-[#A1A1B3] hover:border-[#8B5CF6] hover:text-[#F4F4F6] transition-colors">Home</Link>
          <Link to="/store" data-testid="success-store-link" className="font-mono text-xs uppercase tracking-[0.2em] bg-[#8B5CF6] text-black px-5 py-2.5 hover:bg-[#FFB800] transition-colors">Store</Link>
        </div>
      </div>
    </div>
  );
}
